import React, { useState } from "react"
import { Alert, Badge, Button, Card, CardBody, Input, Table } from "reactstrap"
import { breakdownLabel, colourName, lineName, placeName, quantityLabel, replacedProductLabel, ruleHint, sameProductLabel, sharesProduct, supplierNames } from "./labels"
import { AddProductDialog, ChangeProductDialog, NotesDialog, QuantityDialog, RemoveLineDialog } from "./LineDialogs"
import { CorrectAddLineDialog, CorrectLineDialog, CorrectRemoveLineDialog } from "./AfterShipmentDialogs"
import { mainShipment } from "./shipmentLabels"
import { sheetEditable, sheetShipped } from "./sheetState"
import { UNCONFIRMED_CHANGE } from "./useLogisticsOperation"

// Balls without a catalogue product covering the official ball (Balls TBD included) stay with the club, and so do a Tenis10 event's
// balls of each colour: a recommendation to the club, never shipped (PRD 16 LR15, the canvas verdict F-A).
const tenis10Balls = line => line.family === "balls" && Boolean(line.tenis10Level)
const clubBalls = line => line.family === "balls" && (!line.product || tenis10Balls(line))

// A line without a product has the supplier it resolved: the club for balls, none otherwise.
function Supplier({ line, name, pending, onChange }) {
  if (!line.product || tenis10Balls(line)) {
    if (!line.supplier) return <span className="text-muted">—</span>
    const locked = clubBalls(line)
    return <Badge color="soft-secondary" className="p-2" title={tenis10Balls(line) ? "The club provides the Tenis10 balls"
      : locked ? "MPG needs a catalogue product for the official ball" : undefined}>
      {supplierNames[line.supplier] || line.supplier}{locked && <i className="bx bxs-lock-alt ml-1" aria-hidden="true" />}
    </Badge>
  }
  return <>
    <Input type="select" bsSize="sm" aria-label={`Supplier of ${name}`} value={line.supplier} disabled={pending} onChange={event => onChange(event.target.value)}>
      {Object.entries(supplierNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </Input>
    {line.supplierChosen && <div className="small text-muted">Chosen for this sheet</div>}
  </>
}

// The calculated quantity, the one set by admin and the effective one, of a line or one of its places.
function Quantities({ quantities, unit, label, measures, pending, onSet }) {
  const { setByAdmin } = quantities
  return <>
    <td className="text-right" data-label="Calculated">
      {quantityLabel(quantities.calculated, unit)}
      {quantities.benchmark != null && <div className="small text-muted">benchmark {quantityLabel(quantities.benchmark, unit)}</div>}
    </td>
    <td className="text-right" data-label="Set by admin">
      {setByAdmin ? quantityLabel(setByAdmin.quantity, unit) : <span className="text-muted">—</span>}
      <Button color="link" size="sm" className="p-0 ml-2 align-baseline" disabled={pending} aria-label={`${setByAdmin ? "Edit" : "Set"} the quantity of ${label}`}
        onClick={onSet}>{setByAdmin ? "Edit" : "Set"}</Button>
      {quantities.marks.includes("calculationChanged") && <div className="small text-warning">
        <i className="bx bx-error mr-1" aria-hidden="true" />
        {`Calculation changed since set: ${quantityLabel(setByAdmin.calculated, unit)} → ${quantityLabel(quantities.calculated, unit)}`}
      </div>}
      {/* An event edit replaced the product this quantity was set for (tickets 12 Q9, 15 Q11). */}
      {quantities.replaced && <div className="small text-warning">
        <i className="bx bx-error mr-1" aria-hidden="true" />{replacedProductLabel(quantities.replaced)}
      </div>}
    </td>
    <td className="text-right" data-label="Effective">
      <strong>{quantityLabel(quantities.effective, unit)}</strong>{measures && <div className="small text-muted">{measures}</div>}
    </td>
  </>
}

function LineActions({ name, pending, onNotes, onRemove }) {
  return <td className="text-right text-nowrap" data-label="">
    <Button color="link" size="sm" className="p-1 text-muted" title="Notes" aria-label={`Notes for ${name}`} disabled={pending} onClick={onNotes}>
      <i className="bx bx-note" aria-hidden="true" />
    </Button>
    <Button color="link" size="sm" className="p-1 text-muted" title="Remove line" aria-label={`Remove ${name}`} disabled={pending} onClick={onRemove}>
      <i className="bx bx-trash" aria-hidden="true" />
    </Button>
  </td>
}

// A line whose product another line holds (D21), and what a product's line numbered from 2 says instead of its rule.
function SharedProduct({ line, lines }) {
  return sharesProduct(line, lines) ? <> <Badge color="soft-info">Also on another line</Badge></> : null
}
const ruleDetails = (line, lines) => {
  if (line.instance) return [sameProductLabel(line, lines)]
  return [ruleHint(line.product?.rule, line.unit),
    line.product ? (line.product.rule ? null : "No calculation rule") : clubBalls(line) ? "No catalogue product · provided by the club" : "No catalogue product"]
}

// The product cell's marks and secondary lines: a product another line holds too, a product chosen for this sheet and the change to
// another one that fits, the rule's hint, a missing product or rule, the weight of what MPG sends and the line's notes.
function LineDetails({ line, lines, name, pending, onChangeProduct }) {
  const details = [...ruleDetails(line, lines), line.weight != null ? `${line.weight.toFixed(2)} kg` : null].filter(Boolean)
  return <>
    <SharedProduct line={line} lines={lines} />
    {line.marks.includes("toComplete") && <> <Badge color="soft-danger">To complete</Badge></>}
    {line.marks.includes("toCheck") && <> <Badge color="soft-warning">To check</Badge></>}
    {/* A colour line kept after its colour's last competition left, since an administrator worked on it (Events ADR 0016, round 7 „A”). */}
    {line.marks.includes("toReview") && <> <Badge color="soft-warning">To review</Badge>
      <div className="small text-muted">{`No ${colourName(line.tenis10Level)} competition left on this event: set the quantity to 0 or remove the line.`}</div></>}
    {line.productChosen && <div className="small text-muted">Chosen for this sheet</div>}
    {line.generic && line.alternatives?.length > 0 && <Button color="link" size="sm" className="p-0 d-block" disabled={pending}
      aria-label={`Change the product of ${name}`} onClick={onChangeProduct}>Change product</Button>}
    {details.map(text => <div key={text} className="small text-muted">{text}</div>)}
    {line.notes && <div className="small"><i className="bx bx-note mr-1 text-muted" aria-hidden="true" />{line.notes}</div>}
  </>
}

function ProductLine({ line, lines, officialBall, pending, open, changeSupplier }) {
  const name = lineName(line, officialBall)
  const details = <LineDetails line={line} lines={lines} name={name} pending={pending} onChangeProduct={() => open({ kind: "product", line, name })} />
  const actions = <LineActions name={name} pending={pending} onNotes={() => open({ kind: "notes", line, name })}
    onRemove={() => open({ kind: "remove", line, name })} />
  const supplier = <Supplier line={line} name={name} pending={pending} onChange={value => changeSupplier(line, value)} />
  // Medals have one row per place (Champion, Finalist), each with its own quantity; the supplier,
  // notes and actions are the line's, on the first.
  if (line.variants) {
    return line.variants.map((variant, index) => {
      const label = `${name} · ${placeName(variant.place)}`
      return <tr key={variant.place}>
        <td>{index ? <span className="text-muted">{label}</span>
          : <><strong>{name}</strong> <span className="text-muted">· {placeName(variant.place)}</span>{details}</>}</td>
        <Quantities quantities={variant} unit={line.unit} label={label} pending={pending}
          onSet={() => open({ kind: "quantity", line, name: label, quantities: variant, place: variant.place })} />
        <td data-label="Supplier">{index ? <span className="small text-muted">as above</span> : supplier}</td>
        {index ? <td data-label="" /> : actions}
      </tr>
    })
  }
  // The effective quantity in grouping packages and, for a can of balls, in balls.
  const measures = [
    ...(line.breakdown ? [breakdownLabel(line.breakdown, line.product.grouping, line.unit)] : []),
    ...(line.family === "balls" && line.product && line.pieces != null && line.unit !== "ball" ? [quantityLabel(line.pieces, "ball")] : []),
  ].join(" · ")
  return <tr>
    <td><strong>{name}</strong>{details}</td>
    <Quantities quantities={line} unit={line.unit} label={name} measures={measures} pending={pending}
      onSet={() => open({ kind: "quantity", line, name, quantities: line })} />
    <td data-label="Supplier">{supplier}</td>
    {actions}
  </tr>
}

// What a shipped line or place was at validation, on hover of its locked quantity (US 79).
const atValidationLabel = ({ atValidation }, unit) => atValidation && [`At validation: calculated ${quantityLabel(atValidation.calculated, unit)}`,
  `benchmark ${quantityLabel(atValidation.benchmark, unit)}`, atValidation.setByAdmin && `set by admin ${quantityLabel(atValidation.setByAdmin.quantity, unit)}`]
  .filter(Boolean).join(" · ")

// A locked line, or one of its places: the calculation now and the effective quantity locked at validation; once shipped, what
// supplementary shipments added and returns brought back, and the signal when the calculation now differs from what was sent (US 80).
function LockedQuantities({ quantities, line, shipped }) {
  const sent = quantities.effective + (quantities.supplementary || 0)
  return <>
    <td className="text-right" data-label="Calculated now">{quantityLabel(quantities.calculated, line.unit)}</td>
    <td className="text-right" data-label="Effective">
      <span title={atValidationLabel(quantities, line.unit) || undefined}><strong>{quantityLabel(quantities.effective, line.unit)}</strong></span>
      {quantities.marks.includes("calculationDiffers") && <div className="small text-warning">
        <i className="bx bx-error mr-1" aria-hidden="true" />
        {`Calculated now ${quantityLabel(quantities.calculated, line.unit)} · ${line.supplier === "mpg" ? "sent" : "asked of the club"} ${quantityLabel(sent, line.unit)}`}
      </div>}
    </td>
    {shipped && <>
      <td className="text-right" data-label="Suppl.">{quantities.supplementary ? `+${quantityLabel(quantities.supplementary, line.unit)}` : "—"}</td>
      <td className="text-right" data-label="Returned">{quantities.returned ? quantityLabel(quantities.returned, line.unit) : "—"}</td>
    </>}
  </>
}

// The actions on a shipped line: a correction after shipment, or its removal, each with a reason.
function LockedActions({ name, pending, onCorrect, onRemove }) {
  return <td className="text-right text-nowrap" data-label="">
    <Button color="link" size="sm" className="p-1 text-muted" title="Correction after shipment (reason required)" aria-label={`Correct ${name}`}
      disabled={pending} onClick={onCorrect}><i className="bx bx-edit-alt" aria-hidden="true" /></Button>
    <Button color="link" size="sm" className="p-1 text-muted" title="Remove after shipment (reason required)" aria-label={`Remove ${name} after shipment`}
      disabled={pending} onClick={onRemove}><i className="bx bx-trash" aria-hidden="true" /></Button>
  </td>
}

// The products of a validated or shipped sheet (ticket 13 Q11–Q12, D13): locked. A validated sheet changes only once reopened. A shipped
// one's change is a correction after shipment, with a reason and without a new AWB (`correct`, a command on the shipments): a line's
// quantities, supplier or notes, a product added to the parcel, a line removed.
function LockedProducts({ sheet, pending, correct }) {
  const shipped = sheetShipped(sheet.status)
  const [dialog, setDialog] = useState(null)
  const [failure, setFailure] = useState(null)
  const main = mainShipment(sheet)
  const movesStock = Boolean(main?.movesStock)
  const open = next => { setFailure(null); setDialog(next) }
  const close = () => { setFailure(null); setDialog(null) }
  const submit = body => correct(body).then(result => {
    if (!result || result.status === "cancelled") return
    if (result.status === "saved") close()
    else setFailure(result.status === "unknown" ? UNCONFIRMED_CHANGE : result.message)
  })
  const dialogState = { movesStock, balances: sheet.balances, pending, failure, onSubmit: submit, onClose: close }
  const supplier = line => supplierNames[line.supplier] || line.supplier || "—"
  return <Card tag="section" aria-label="Products" id="logistics-products"><CardBody>
    <h4 className="card-title mb-1">Products <i className="bx bxs-lock-alt text-muted" aria-hidden="true" /></h4>
    <p className="text-muted small">{shipped ? "Locked at validation. A correction after shipment comes with a reason; to send more, a supplementary shipment."
      : "Locked at the sheet's validation. Reopen sheet unlocks them."}</p>
    <Table size="sm" className="logistics-stack mb-0">
      <thead><tr>
        <th>Product</th><th className="text-right">Calculated now</th><th className="text-right">Effective<i className="bx bxs-lock-alt ml-1" aria-hidden="true" /></th>
        {shipped && <><th className="text-right">Suppl.</th><th className="text-right">Returned</th></>}<th>Supplier</th>
        {shipped && <th><span className="sr-only">Actions</span></th>}
      </tr></thead>
      <tbody>{sheet.lines.flatMap(line => {
        const name = lineName(line, sheet.officialBall)
        // The event now takes another product than the one shipped, or none: only signalled (US 12).
        const details = <>
          <SharedProduct line={line} lines={sheet.lines} />
          {line.instance && <div className="small text-muted">{sameProductLabel(line, sheet.lines)}</div>}
          {line.marks.includes("eventChanged") && <div className="small text-warning"><i className="bx bx-error mr-1" aria-hidden="true" />
            {`The event now takes ${line.eventProduct ? line.eventProduct.name : "no catalogue product for this line"}; the sheet keeps what was sent.`}</div>}
          {line.notes && <div className="small"><i className="bx bx-note mr-1 text-muted" aria-hidden="true" />{line.notes}</div>}
        </>
        const actions = shipped && <LockedActions name={name} pending={pending} onCorrect={() => open({ kind: "correct", line, name })}
          onRemove={() => open({ kind: "remove", line, name })} />
        if (!line.variants) {
          return [<tr key={line.key}><td><strong>{name}</strong>{details}</td><LockedQuantities quantities={line} line={line} shipped={shipped} />
            <td data-label="Supplier">{supplier(line)}</td>{actions}</tr>]
        }
        return line.variants.map((variant, index) => <tr key={`${line.key}-${variant.place}`}>
          <td>{index ? <span className="text-muted">{`${name} · ${placeName(variant.place)}`}</span>
            : <><strong>{name}</strong> <span className="text-muted">· {placeName(variant.place)}</span>{details}</>}</td>
          <LockedQuantities quantities={variant} line={line} shipped={shipped} />
          <td data-label="Supplier">{index ? <span className="small text-muted">as above</span> : supplier(line)}</td>
          {shipped && (index ? <td data-label="" /> : actions)}
        </tr>)
      })}</tbody>
    </Table>
    {shipped && <div className="d-flex justify-content-between flex-wrap pt-2 border-top">
      <Button color="link" className="p-0" disabled={pending} onClick={() => open({ kind: "add" })}>
        <i className="bx bx-plus mr-1" aria-hidden="true" />Add product to the parcel
      </Button>
    </div>}
    {failure && !dialog && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    {dialog?.kind === "correct" && <CorrectLineDialog line={dialog.line} name={dialog.name} {...dialogState} />}
    {dialog?.kind === "remove" && <CorrectRemoveLineDialog line={dialog.line} name={dialog.name} {...dialogState} />}
    {dialog?.kind === "add" && <CorrectAddLineDialog addable={sheet.addable || []} {...dialogState} />}
  </CardBody></Card>
}

// The sheet's products: the calculated quantity with its capacity benchmark, the quantity set by
// admin and the effective one, the product of a standard line, the supplier, the notes, and the lines added or removed. Each change
// is one line command (`changeLine`), confirmed by Events before the sheet reloads. A shipped sheet's products are locked; they change
// only by a correction after shipment (`correct`).
export default function SheetProducts({ sheet, pending, changeLine, correct }) {
  return sheetEditable(sheet.status) ? <EditableProducts sheet={sheet} pending={pending} changeLine={changeLine} />
    : <LockedProducts sheet={sheet} pending={pending} correct={correct} />
}

function EditableProducts({ sheet, pending, changeLine }) {
  const [dialog, setDialog] = useState(null)
  const [failure, setFailure] = useState(null)
  const open = next => { setFailure(null); setDialog(next) }
  const close = () => { setFailure(null); setDialog(null) }
  const submit = body => changeLine(body).then(result => {
    if (!result || result.status === "cancelled") return
    if (result.status === "saved") close()
    else setFailure(result.status === "unknown" ? UNCONFIRMED_CHANGE : result.message)
  })
  // The submit of a dialog opened on a line or on one of its places.
  const submitFor = (line, place) => body => submit({ ...body, line: line.key, ...(place ? { place } : {}) })
  const changeSupplier = (line, supplier) => { setFailure(null); submit({ action: "setSupplier", line: line.key, supplier }) }
  const dialogState = { pending, failure, onClose: close }

  return <Card tag="section" aria-label="Products" id="logistics-products"><CardBody>
    <h4 className="card-title mb-1">Products</h4>
    <p className="text-muted small">Effective = set by admin if present, otherwise calculated. The capacity benchmark is for orientation only.</p>
    <Table size="sm" className="logistics-stack mb-0">
      <thead><tr>
        <th>Product</th><th className="text-right">Calculated</th><th className="text-right">Set by admin</th><th className="text-right">Effective</th>
        <th>Supplier</th><th><span className="sr-only">Actions</span></th>
      </tr></thead>
      <tbody>{sheet.lines.map(line => <ProductLine key={line.key} line={line} lines={sheet.lines} officialBall={sheet.officialBall} pending={pending}
        open={open} changeSupplier={changeSupplier} />)}</tbody>
    </Table>
    <div className="d-flex justify-content-between flex-wrap pt-2 border-top">
      <Button color="link" className="p-0" disabled={pending} onClick={() => open({ kind: "add" })}>
        <i className="bx bx-plus mr-1" aria-hidden="true" />Add product from catalog
      </Button>
    </div>
    {failure && !dialog && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    {dialog?.kind === "quantity" && <QuantityDialog name={dialog.name} unit={dialog.line.unit} quantities={dialog.quantities} notes={dialog.line.notes}
      onSubmit={submitFor(dialog.line, dialog.place)} {...dialogState} />}
    {dialog?.kind === "notes" && <NotesDialog name={dialog.name} notes={dialog.line.notes} onSubmit={submitFor(dialog.line)} {...dialogState} />}
    {dialog?.kind === "remove" && <RemoveLineDialog name={dialog.name} generic={dialog.line.generic} onSubmit={submitFor(dialog.line)} {...dialogState} />}
    {dialog?.kind === "product" && <ChangeProductDialog name={dialog.name} alternatives={dialog.line.alternatives}
      quantitiesSet={Boolean(dialog.line.setByAdmin || dialog.line.variants?.some(variant => variant.setByAdmin))} onSubmit={submitFor(dialog.line)}
      {...dialogState} />}
    {dialog?.kind === "add" && <AddProductDialog addable={sheet.addable || []} onSubmit={submit} {...dialogState} />}
  </CardBody></Card>
}
